import { useCallback, useState } from 'react';

import { window } from '../../../globals';

import useUpdateOrganizationSBOMReportDefaults from './useUpdateOrganizationSBOMReportDefaults';

type SBOMReportDefaults = {
  sbomReportAuthorName: string;
  sbomReportAuthorEmail: string;
  sbomReportSupplier: string;
};

const toEditableValue = (value: string | null | undefined): string => value ?? '';
const toPayloadValue = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue === '' ? null : trimmedValue;
};

/**
 * Shared hook for managing organization-level SBOM report defaults.
 */
export function useSBOMReportDefaultsSettings() {
  const [settings, setSettings] = useState<SBOMReportDefaults>({
    sbomReportAuthorName: toEditableValue(window.user?.organization?.sbomReportAuthorName),
    sbomReportAuthorEmail: toEditableValue(window.user?.organization?.sbomReportAuthorEmail),
    sbomReportSupplier: toEditableValue(window.user?.organization?.sbomReportSupplier),
  });

  const { mutateAsync: updateSBOMReportDefaults, isPending: isSaving } = useUpdateOrganizationSBOMReportDefaults();

  const currentOrganizationSettings = {
    sbomReportAuthorName: toEditableValue(window.user?.organization?.sbomReportAuthorName),
    sbomReportAuthorEmail: toEditableValue(window.user?.organization?.sbomReportAuthorEmail),
    sbomReportSupplier: toEditableValue(window.user?.organization?.sbomReportSupplier),
  };

  const isDirty =
    settings.sbomReportAuthorName !== currentOrganizationSettings.sbomReportAuthorName ||
    settings.sbomReportAuthorEmail !== currentOrganizationSettings.sbomReportAuthorEmail ||
    settings.sbomReportSupplier !== currentOrganizationSettings.sbomReportSupplier;

  const isSaveDisabled = !isDirty || isSaving || !window.user?.organizationId;

  const onUpdate = useCallback((update: Partial<SBOMReportDefaults>) => {
    setSettings((previousSettings) => ({ ...previousSettings, ...update }));
  }, []);

  const onSave = useCallback(async () => {
    const organizationId = window.user?.organizationId;
    if (!organizationId) {
      return;
    }

    try {
      await updateSBOMReportDefaults({
        organizationId,
        sbomReportAuthorName: toPayloadValue(settings.sbomReportAuthorName),
        sbomReportAuthorEmail: toPayloadValue(settings.sbomReportAuthorEmail),
        sbomReportSupplier: toPayloadValue(settings.sbomReportSupplier),
      });
    } catch {
      // Error handling is done by useToastedMutation in the mutation hook.
    }
  }, [settings, updateSBOMReportDefaults]);

  const placeholders = {
    sbomReportAuthorName: `${window.user?.organization?.title || 'FOSSA'} Security Team`,
    sbomReportAuthorEmail: window.user?.email || 'sbom@example.com',
    sbomReportSupplier: window.user?.organization?.title || 'FOSSA, Inc.',
  };

  return {
    settings,
    placeholders,
    isSaving,
    isDirty,
    isSaveDisabled,
    onUpdate,
    onSave,
  };
}
